import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiUsers, FiSearch, FiDownload, FiPrinter,
  FiBarChart2, FiDollarSign, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatCurrency, formatNumber, formatDate as fmtDate } from '../../utils/formatters';
import { recordReads } from '../../utils/readTelemetry';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDate = (v) => fmtDate(v) || '-';

// A "bill" is any sale document: tax invoice, non-tax invoice, or a POS sale.
const BILL_CODES = ['SI', 'NSI', 'PS'];
// A "payment" is money received from the customer.
const PAYMENT_CODES = ['CR', 'BR'];

const BILL_ROUTE = { SI: '/sales/sale-invoice', NSI: '/sales/non-tax-invoice', PS: '/pos' };
const PAY_ROUTE = { CR: '/receipts/cash-receipt', BR: '/receipts/bank-receipt' };

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const today = new Date();
  return Math.max(0, Math.floor((today - d) / 86400000));
};

const CustomerBalances = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('customer-balances');
  const navigate = useNavigate();

  // Data state
  const [accounts, setAccounts] = useState(() => savedMem?.accounts ?? []);
  const [subTransactions, setSubTransactions] = useState(() => savedMem?.subTransactions ?? []);
  const [lastDocs, setLastDocs] = useState(() => savedMem?.lastDocs ?? { bills: {}, payments: {} });
  const [customers, setCustomers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? new Date().toISOString().split('T')[0]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => savedMem?.selectedCustomerId ?? 'all');
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? 'all');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? 'all');
  const [selectedSalesPersonId, setSelectedSalesPersonId] = useState(() => savedMem?.selectedSalesPersonId ?? 'all');
  const [minBalance, setMinBalance] = useState(() => savedMem?.minBalance ?? '');
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'name');
  const [sortDir, setSortDir] = useState(() => savedMem?.sortDir ?? 'asc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // Set default FY + date range — ONLY on a fresh visit. On a restored view
      // (Back navigation) keep the restored selectedFYId / dateFrom.
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
          // Set date range from active FY
          const activeFY = fys.find(f => f.id === activeFinancialYear.id);
          if (activeFY) {
            setDateFrom(activeFY.startDate || '');
          }
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
          setDateFrom(fys[0].startDate || '');
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'customers');
      const snapshot = await getDocs(ref);
      recordReads(activeCompany.id, snapshot.size);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // Load regions
  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      recordReads(activeCompany.id, snapshot.size);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  // Load areas
  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      recordReads(activeCompany.id, snapshot.size);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  // Load employees (for salesman filter)
  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees([...rows]);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  // Load accounts (customer linked)
  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return [];
    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const q = query(accountsRef, where('linkedEntityType', '==', 'customer'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error loading accounts:', error);
      return [];
    }
  }, [activeCompany]);

  // ── Last bill / last payment (current financial year) ──
  // "Any bill" = Sale Invoice, Non-Tax Sale Invoice, or POS Sale.
  // "Payment"  = Cash Receipt or Bank Receipt.
  const loadLastDocs = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return { bills: {}, payments: {} };
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');
      // One query for the whole year, then work out the latest per customer in a
      // single pass — no per-customer lookups, so it stays fast with 500+ customers.
      // Scoped to the 5 doc types this actually reads (BILL_CODES + PAYMENT_CODES).
      // Without it the query downloaded the ENTIRE transactions collection for the
      // year — purchases, journals, GRNs, everything — only to throw all of it away
      // in the loop below. 5 values, well inside Firestore's 30-value `in` limit.
      const snap = await getDocs(query(
        ref,
        where('financialYearId', '==', selectedFYId),
        where('tranTypeCode', 'in', [...BILL_CODES, ...PAYMENT_CODES])
      ));
      recordReads(activeCompany.id, snap.size);
      const bills = {};
      const payments = {};
      snap.docs.forEach(d => {
        const t = { id: d.id, ...d.data() };
        if (!t.partyId) return;
        if (t.status === 'Draft' || t.status === 'Cancelled') return;
        if (activeBranch && activeBranch.id !== 'all' && t.branchId !== activeBranch.id) return;
        const date = t.docDate || '';
        if (!date) return;

        if (BILL_CODES.includes(t.tranTypeCode)) {
          const cur = bills[t.partyId];
          if (!cur || date > cur.docDate) {
            bills[t.partyId] = { id: t.id, docNumber: t.docNumber || '', docDate: date, code: t.tranTypeCode };
          }
        } else if (PAYMENT_CODES.includes(t.tranTypeCode)) {
          const cur = payments[t.partyId];
          if (!cur || date > cur.docDate) {
            payments[t.partyId] = {
              id: t.id, docNumber: t.docNumber || '', docDate: date,
              amount: Number(t.totalAmount) || 0, code: t.tranTypeCode,
            };
          }
        }
      });
      return { bills, payments };
    } catch (error) {
      console.error('Error loading last bill/payment:', error);
      return { bills: {}, payments: {} };
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  // Load subTransactions
  const loadSubTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return [];
    try {
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const q = query(
        subTransRef,
        where('status', 'in', ['Posted', 'Draft']),
        where('financialYearId', '==', selectedFYId)
      );
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(t => {
          // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
          if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
    } catch (error) {
      console.error('Error loading subTransactions:', error);
      return [];
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => {
    loadFinancialYears();
    loadCustomers();
    loadRegions();
    loadAreas();
    loadEmployees();
  }, [loadFinancialYears, loadCustomers, loadRegions, loadAreas, loadEmployees]);

  // Update dateFrom when FY changes — skip on a restored view so the saved
  // dateFrom survives (fresh visits behave exactly as before).
  useEffect(() => {
    if (savedMem) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) {
      setDateFrom(fy.startDate || '');
    }
  }, [selectedFYId, financialYears, savedMem]);

  // Generate report
  const handleGenerateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    setHasGenerated(true);
    try {
      const [accs, trans, docs] = await Promise.all([
        loadAccounts(),
        loadSubTransactions(),
        loadLastDocs()
      ]);
      setAccounts(accs);
      setSubTransactions(trans);
      setLastDocs(docs);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Backward memory: once generated, keep a snapshot in sync as the user tweaks
  // the (display-only) filters, so Back restores the exact view with 0 re-reads.
  useEffect(() => {
    if (!hasGenerated) return;
    saveMemory({
      accounts, subTransactions, lastDocs, hasGenerated: true,
      selectedFYId, dateFrom, dateTo, selectedCustomerId, selectedRegionId,
      selectedAreaId, selectedSalesPersonId, minBalance, searchQuery, sortField, sortDir
    });
  }, [hasGenerated, accounts, subTransactions, lastDocs, selectedFYId, dateFrom, dateTo,
      selectedCustomerId, selectedRegionId, selectedAreaId, selectedSalesPersonId,
      minBalance, searchQuery, sortField, sortDir, saveMemory]);

  // Build customer map
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Build accountId → customerId mapping
  const accountToCustomer = useMemo(() => {
    const map = {};
    accounts.forEach(acc => {
      if (acc.linkedEntityId) {
        map[acc.id] = acc.linkedEntityId;
      }
    });
    return map;
  }, [accounts]);

  // Build customerId → accountId mapping
  const customerToAccount = useMemo(() => {
    const map = {};
    accounts.forEach(acc => {
      if (acc.linkedEntityId) {
        map[acc.linkedEntityId] = acc.id;
      }
    });
    return map;
  }, [accounts]);

  // Set of customer account IDs for quick lookup
  const customerAccountIds = useMemo(() => {
    return new Set(accounts.map(a => a.id));
  }, [accounts]);

  // Compute balances per customer
  const customerBalances = useMemo(() => {
    const balances = {}; // customerId → { opening, debit, credit }

    // Initialize all customers that have linked accounts
    accounts.forEach(acc => {
      if (acc.linkedEntityId) {
        balances[acc.linkedEntityId] = { opening: 0, debit: 0, credit: 0 };
      }
    });

    // Process sub-transactions
    subTransactions.forEach(t => {
      if (!customerAccountIds.has(t.accountId)) return;
      const customerId = accountToCustomer[t.accountId];
      if (!customerId || !balances[customerId]) return;

      const docDate = t.docDate || '';

      if (dateFrom && docDate < dateFrom) {
        // Before date range → opening balance (asset account: debit increases)
        balances[customerId].opening += (t.debitAmount || 0) - (t.creditAmount || 0);
      } else if ((!dateFrom || docDate >= dateFrom) && (!dateTo || docDate <= dateTo)) {
        // Within date range
        balances[customerId].debit += (t.debitAmount || 0);
        balances[customerId].credit += (t.creditAmount || 0);
      }
    });

    return balances;
  }, [subTransactions, accounts, customerAccountIds, accountToCustomer, dateFrom, dateTo]);

  // Filtered areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Build report rows
  const reportRows = useMemo(() => {
    const rows = [];

    Object.keys(customerBalances).forEach(customerId => {
      const customer = customerMap[customerId];
      if (!customer) return;

      const bal = customerBalances[customerId];
      const closing = bal.opening + bal.debit - bal.credit;

      const lastBill = lastDocs.bills[customerId] || null;
      const lastPay = lastDocs.payments[customerId] || null;

      rows.push({
        customerId,
        accountId: customerToAccount[customerId],
        // Last bill (any sale doc) + last payment, within the selected financial year
        lastBill,
        lastPay,
        daysSinceBill: lastBill ? daysSince(lastBill.docDate) : null,
        name: customer.companyName || customer.name || '',
        regionId: customer.regionId || '',
        regionName: '',
        areaId: customer.areaId || '',
        areaName: '',
        salesPersonId: customer.salesPersonId || '',
        salesPersonName: customer.salesPersonName || '',
        phone: customer.phone || customer.mobile || '',
        creditLimit: customer.creditLimit || 0,
        opening: bal.opening,
        debit: bal.debit,
        credit: bal.credit,
        closing
      });
    });

    // Enrich with region/area names
    const regionMap = {};
    regions.forEach(r => { regionMap[r.id] = r.name || r.regionName || ''; });
    const areaMap = {};
    areas.forEach(a => { areaMap[a.id] = a.name || a.areaName || ''; });

    rows.forEach(row => {
      row.regionName = regionMap[row.regionId] || '';
      row.areaName = areaMap[row.areaId] || '';
    });

    return rows;
  }, [customerBalances, customerMap, customerToAccount, regions, areas, lastDocs]);

  // Apply filters and search
  const filteredRows = useMemo(() => {
    let rows = [...reportRows];

    // Customer filter
    if (selectedCustomerId !== 'all') {
      rows = rows.filter(r => r.customerId === selectedCustomerId);
    }

    // Region filter
    if (selectedRegionId !== 'all') {
      rows = rows.filter(r => r.regionId === selectedRegionId);
    }

    // Area filter
    if (selectedAreaId !== 'all') {
      rows = rows.filter(r => r.areaId === selectedAreaId);
    }

    // Salesman / employee filter
    if (selectedSalesPersonId !== 'all') {
      rows = rows.filter(r => r.salesPersonId === selectedSalesPersonId);
    }

    // Min balance
    if (minBalance !== '' && !isNaN(Number(minBalance))) {
      const threshold = Number(minBalance);
      rows = rows.filter(r => Math.abs(r.closing) >= threshold);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.regionName.toLowerCase().includes(q) ||
        r.areaName.toLowerCase().includes(q)
      );
    }

    // Sort
    rows.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'name': valA = a.name.toLowerCase(); valB = b.name.toLowerCase(); break;
        case 'region': valA = a.regionName.toLowerCase(); valB = b.regionName.toLowerCase(); break;
        case 'area': valA = a.areaName.toLowerCase(); valB = b.areaName.toLowerCase(); break;
        case 'opening': valA = a.opening; valB = b.opening; break;
        case 'debit': valA = a.debit; valB = b.debit; break;
        case 'credit': valA = a.credit; valB = b.credit; break;
        case 'closing': valA = a.closing; valB = b.closing; break;
        // Never-billed customers sort last, so the "hasn't bought in ages" ones surface.
        case 'daysSinceBill': valA = a.daysSinceBill ?? -1; valB = b.daysSinceBill ?? -1; break;
        default: valA = a.name.toLowerCase(); valB = b.name.toLowerCase();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [reportRows, selectedCustomerId, selectedRegionId, selectedAreaId, selectedSalesPersonId, minBalance, searchQuery, sortField, sortDir]);

  // Summary
  const summary = useMemo(() => {
    let totalReceivable = 0;
    let totalDebit = 0;
    let totalCredit = 0;
    let count = 0;

    filteredRows.forEach(row => {
      totalReceivable += row.closing;
      totalDebit += row.debit;
      totalCredit += row.credit;
      if (Math.abs(row.closing) > 0.01) count++;
    });

    return { totalCustomers: count, totalReceivable, totalDebit, totalCredit };
  }, [filteredRows]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />;
  };

  // Navigate to ledger for customer
  const handleCustomerClick = (row) => {
    if (row.accountId) {
      navigate('/reports/ledger', {
        state: { filterAccountId: row.accountId, filterAccountName: row.name }
      });
    }
  };

  // Open the actual bill / receipt document behind the last-bill / last-payment cell.
  const openDoc = (route, txnId) => {
    if (!route || !txnId) return;
    navigate(route, { state: { viewTransactionId: txnId } });
  };

  // CSV Export
  const handleExport = () => {
    if (filteredRows.length === 0) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Customer Balances Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}\n`;
    csv += `Generated: ${formatDate(new Date().toISOString().split('T')[0])}\n\n`;
    csv += '#,Customer,Region,Area,Opening,Debit,Credit,Closing,Last Bill No,Last Bill Date,Days Since Bill,Last Payment Date,Last Payment Amount\n';

    filteredRows.forEach((row, i) => {
      csv += `${i + 1},"${row.name.replace(/"/g, '""')}","${row.regionName}","${row.areaName}",`;
      csv += `${row.opening.toFixed(2)},${row.debit.toFixed(2)},${row.credit.toFixed(2)},${row.closing.toFixed(2)},`;
      csv += `"${row.lastBill?.docNumber || ''}","${row.lastBill?.docDate || ''}",${row.daysSinceBill ?? ''},`;
      csv += `"${row.lastPay?.docDate || ''}",${row.lastPay ? row.lastPay.amount.toFixed(2) : ''}\n`;
    });

    csv += `\n,,,,${summary.totalReceivable.toFixed(2)},${summary.totalDebit.toFixed(2)},${summary.totalCredit.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Customer_Balances_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Customer Balances</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:22px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary{display:flex;gap:30px;margin-bottom:20px;padding:15px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:11px;color:#666;margin-bottom:4px}
        .summary-value{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase}
        th.amount{text-align:right}
        td{padding:6px 12px;border-bottom:1px solid #ddd;font-size:12px}
        td.amount{text-align:right;font-family:'Consolas',monospace}
        .total-row{background:#800020;color:white;font-weight:700;font-size:13px}
        .total-row td{border-bottom:none;padding:10px 12px}
        .positive{color:#1565C0} .negative{color:#C62828}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Customer Balances</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)} |
        Generated: ${formatDate(new Date().toISOString().split('T')[0])}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Customers with Balance</div>
          <div class="summary-value">${summary.totalCustomers}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Receivable</div>
          <div class="summary-value">${formatAmount(summary.totalReceivable)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Debit</div>
          <div class="summary-value">${formatAmount(summary.totalDebit)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Credit</div>
          <div class="summary-value">${formatAmount(summary.totalCredit)}</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>#</th>
          <th>Customer</th>
          <th>Region</th>
          <th>Area</th>
          <th class="amount">Opening</th>
          <th class="amount">Debit</th>
          <th class="amount">Credit</th>
          <th class="amount">Closing</th>
          <th>Last Bill No</th>
          <th>Last Bill Date</th>
          <th class="amount">Days</th>
          <th>Last Payment</th>
          <th class="amount">Paid Amt</th>
        </tr></thead>
        <tbody>
          ${filteredRows.map((row, i) => `<tr>
            <td>${i + 1}</td>
            <td>${row.name}</td>
            <td>${row.regionName}</td>
            <td>${row.areaName}</td>
            <td class="amount">${formatAmount(row.opening)}</td>
            <td class="amount">${formatAmount(row.debit)}</td>
            <td class="amount">${formatAmount(row.credit)}</td>
            <td class="amount ${row.closing >= 0 ? 'positive' : 'negative'}">${formatAmount(row.closing)}</td>
            <td>${row.lastBill?.docNumber || '-'}</td>
            <td>${row.lastBill ? formatDate(row.lastBill.docDate) : '-'}</td>
            <td class="amount">${row.daysSinceBill == null ? '-' : row.daysSinceBill + 'd'}</td>
            <td>${row.lastPay ? formatDate(row.lastPay.docDate) : 'Never'}</td>
            <td class="amount">${row.lastPay ? formatAmount(row.lastPay.amount) : '-'}</td>
          </tr>`).join('')}
          <tr class="total-row">
            <td colspan="4" style="text-align:right">Grand Total</td>
            <td class="amount">${formatAmount(filteredRows.reduce((s, r) => s + r.opening, 0))}</td>
            <td class="amount">${formatAmount(summary.totalDebit)}</td>
            <td class="amount">${formatAmount(summary.totalCredit)}</td>
            <td class="amount">${formatAmount(summary.totalReceivable)}</td>
            <td colspan="5"></td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Customer Balances" breadcrumb="Reports / Sale Reports / Customer Balances">
      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              onChange={setSelectedCustomerId}
              allOption={{ value: 'all', label: 'All Customers' }}
            />
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.name || r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.name || a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Salesman</Label>
            <Select value={selectedSalesPersonId} onChange={e => setSelectedSalesPersonId(e.target.value)}>
              <option value="all">All Salesmen</option>
              {employees
                .filter(e => e.isSalesman)
                .map(e => (
                  <option key={e.id} value={e.id}>{e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim()}</option>
                ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Min Balance</Label>
            <Input
              type="number"
              placeholder="0"
              value={minBalance}
              onChange={e => setMinBalance(e.target.value)}
              style={{ width: 120 }}
            />
          </FilterGroup>
          <SearchWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search customer name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchWrap>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={filteredRows.length === 0}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint} disabled={filteredRows.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
              <FiBarChart2 /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Summary Cards */}
      {hasGenerated && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon color={colors.primary.main}><FiUsers /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Customers with Balance</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalCustomers)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#1565C0"><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Receivable</SummaryLabel>
              <SummaryValue style={{ color: '#1565C0' }}>{formatCurrency(summary.totalReceivable)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color={colors.success.main}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>Dr</span>
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Debit</SummaryLabel>
              <SummaryValue style={{ color: colors.success.main }}>{formatCurrency(summary.totalDebit)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#C62828">
              <span style={{ fontSize: 16, fontWeight: 700 }}>Cr</span>
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Credit</SummaryLabel>
              <SummaryValue style={{ color: '#C62828' }}>{formatCurrency(summary.totalCredit)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Loading customer balances...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiUsers /></EmptyIcon>
              <EmptyText>Select a financial year and click Generate to view customer balances</EmptyText>
            </EmptyState>
          ) : filteredRows.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>No customer balances found for the selected criteria</EmptyText>
              <p style={{ fontSize: '14px', color: colors.text.tertiary, marginTop: 8 }}>
                Try adjusting your filters or date range
              </p>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 50 }}>#</Th>
                  <Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    Customer <SortIcon field="name" />
                  </Th>
                  <Th onClick={() => handleSort('region')} style={{ cursor: 'pointer' }}>
                    Region <SortIcon field="region" />
                  </Th>
                  <Th onClick={() => handleSort('area')} style={{ cursor: 'pointer' }}>
                    Area <SortIcon field="area" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('opening')} style={{ cursor: 'pointer', width: 140 }}>
                    Opening <SortIcon field="opening" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('debit')} style={{ cursor: 'pointer', width: 140 }}>
                    Debit <SortIcon field="debit" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('credit')} style={{ cursor: 'pointer', width: 140 }}>
                    Credit <SortIcon field="credit" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('closing')} style={{ cursor: 'pointer', width: 140 }}>
                    Closing <SortIcon field="closing" />
                  </Th>
                  <Th>Last Bill No</Th>
                  <Th>Last Bill Date</Th>
                  <Th align="right" onClick={() => handleSort('daysSinceBill')} style={{ cursor: 'pointer' }}>
                    Days Since Bill <SortIcon field="daysSinceBill" />
                  </Th>
                  <Th>Last Payment</Th>
                  <Th align="right">Payment Amount</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRows.map((row, index) => (
                  <Tr key={row.customerId}>
                    <Td>{index + 1}</Td>
                    <Td>
                      <ClickableName onClick={() => handleCustomerClick(row)}>
                        {row.name}
                      </ClickableName>
                    </Td>
                    <Td>{row.regionName}</Td>
                    <Td>{row.areaName}</Td>
                    <AmountCell align="right">
                      {row.opening !== 0 ? formatAmount(row.opening) : '-'}
                    </AmountCell>
                    <AmountCell align="right">
                      {row.debit > 0 ? formatAmount(row.debit) : '-'}
                    </AmountCell>
                    <AmountCell align="right">
                      {row.credit > 0 ? formatAmount(row.credit) : '-'}
                    </AmountCell>
                    <AmountCell align="right" $isClosing $positive={row.closing >= 0}>
                      {formatAmount(row.closing)}
                    </AmountCell>
                    <Td>
                      {row.lastBill ? (
                        <ClickableName onClick={() => openDoc(BILL_ROUTE[row.lastBill.code], row.lastBill.id)}>
                          {row.lastBill.docNumber}
                        </ClickableName>
                      ) : <Muted>—</Muted>}
                    </Td>
                    <Td>{row.lastBill ? formatDate(row.lastBill.docDate) : <Muted>—</Muted>}</Td>
                    <Td align="right">
                      {row.daysSinceBill == null
                        ? <Muted>—</Muted>
                        : <DaysChip $stale={row.daysSinceBill > 60 && row.closing > 0}>{row.daysSinceBill}d</DaysChip>}
                    </Td>
                    <Td>
                      {row.lastPay ? (
                        <ClickableName onClick={() => openDoc(PAY_ROUTE[row.lastPay.code], row.lastPay.id)}>
                          {formatDate(row.lastPay.docDate)}
                        </ClickableName>
                      ) : <Muted>Never</Muted>}
                    </Td>
                    <AmountCell align="right">
                      {row.lastPay ? formatAmount(row.lastPay.amount) : '-'}
                    </AmountCell>
                  </Tr>
                ))}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                    Grand Total
                  </Td>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(filteredRows.reduce((s, r) => s + r.opening, 0))}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalDebit)}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalCredit)}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalReceivable)}
                  </AmountCell>
                  {/* Last bill / last payment columns have no meaningful total */}
                  <Td colSpan={5} />
                </TotalRow>
              </Tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// Styled Components
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
  &::placeholder { color: ${colors.text.tertiary}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    border-color: ${colors.primary.main};
    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover:not(:disabled) {
      background: ${colors.neutral[100]};
      border-color: ${colors.primary.main};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  &:hover {
    color: ${colors.primary.main};
  }
`;

const Tbody = styled.tbody``;
const Tfoot = styled.tfoot``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;
  &:hover { background: ${colors.neutral[50]}; }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;

  ${props => props.$isTotal && `
    font-weight: 700;
    font-size: 14px;
    color: ${colors.text.primary};
  `}

  ${props => props.$isClosing && `
    font-weight: 600;
    color: ${props.$positive ? '#1565C0' : '#C62828'};
  `}
`;

const ClickableName = styled.span`
  cursor: pointer;
  color: #1a73e8;
  &:hover {
    text-decoration: underline;
  }
`;

const Muted = styled.span`color: #bbb;`;

// Turns red once a customer who OWES money hasn't bought in a while — the
// "chase this one" signal.
const DaysChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${p => (p.$stale ? '#FFEBEE' : colors.neutral[100])};
  color: ${p => (p.$stale ? '#C62828' : colors.text.secondary)};
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};
  td { padding: 14px; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default CustomerBalances;
